import { CompanyTabType, Article, WithId, ContentfulCompany } from '../types'
import { getArticlesByCompany, getContentfulCompany } from './api/contentful'
import { convertArticle } from './article'
import {
  CompanyData,
  PrivateCompanyData,
  IPOCompanyData,
  CompanyRecordType,
  OverseasCompanyData,
  CompanyMemo,
} from '../types/api'
import { printError } from './log'

export const companyTabLabel: { [type in CompanyTabType]: string } = {
  'basic-info': '企業概要',
  'finance-info': 'ファイナンス',
  news: 'ニュース',
  shareholders: '株主',
  directors: '役員',
  performances: '業績',
  employees: '従業員数',
  partnerships: '提携先',
  benchmarks: '類似企業',
  others: 'その他',
  'ipo-data': 'IPOデータ',
}

export const isDomesticCompanyData = (
  company: CompanyData | OverseasCompanyData
): company is CompanyData => {
  return typeof company.shortname !== 'undefined'
}

export const isPrivate = (
  company: CompanyData
): company is PrivateCompanyData => {
  return company.recordType === '未公開企業'
}

export const isIPO = (company: CompanyData): company is IPOCompanyData => {
  return company.recordType === 'IPO企業'
}

export const isResearchContinue = (
  company: CompanyData
): company is PrivateCompanyData => {
  return isPrivate(company) && /調査継続/.test(company.researchStatus)
}

export const isResearchUnvalued = (
  company: CompanyData
): company is PrivateCompanyData => {
  return isPrivate(company) && /未評価/.test(company.researchStatus)
}

export const isExited = (company: CompanyData) => {
  return !!company.exited
}

export const isResearchDone = (company: CompanyData) => {
  return !isResearchContinue(company) && !isResearchUnvalued(company)
}

export const companyDefaultImage = (type: CompanyRecordType) => {
  return type === '未公開企業'
    ? '/static/images/default/company.png'
    : '/static/images/default/ipo.png'
}

export const fetchArticlesByCompany = async (
  companyId: string
): Promise<Article[]> => {
  let articles: Article[] = []
  try {
    const data = await getArticlesByCompany(companyId)
    articles = data.items.map((res) =>
      convertArticle({ ...res.fields, id: res.sys.id })
    )
  } catch (e) {
    printError(e)
  }
  return articles
}

export const fetchContentfulCompany = async (shortname: string) => {
  let company: WithId<ContentfulCompany> | null = null
  try {
    const data = await getContentfulCompany(shortname)
    if (data) {
      company = data
    }
  } catch (e) {
    printError(e)
  }
  return company
}

export const checkMemoUpdatedWithinOneWeek = (
  companyMemo: CompanyMemo | null
) => {
  if (companyMemo == null) return false
  const day = new Date()
  day.setDate(day.getDate() - 7)
  return day < new Date(companyMemo.updated)
}

export const splitMemoContentWithHyperLinkForCompany = (
  companyMemo: CompanyMemo | null
) => {
  if (!companyMemo || !companyMemo.content) return []
  const urlRegex = /(https?:\/\/[\w/:%#\$&\?\(\)~\.=\+\-]+)/g
  const nonUrlRegex = /[^:\w!?/+\-_~;.,*&@#$%()'[\]]+/g

  if (!urlRegex.test(companyMemo.content)) return [companyMemo.content]

  const putContentPart = (str: string, contentParts: string[]): string[] => {
    const idx = str.search(isHyperLink(str) ? nonUrlRegex : urlRegex)
    if (idx === -1) return contentParts.concat(str)
    const firstStr = str.slice(0, idx)
    const secondStr = str.slice(idx)
    return putContentPart(secondStr, contentParts.concat(firstStr))
  }

  return putContentPart(companyMemo.content, [])
}

export const splitMemoContentWithHyperLink = (
  companyMemo:
    | {
        content: string
        updated: string
        lastUpdateUserId: number
        lastName: string
        firstName: string
      }
    | undefined
) => {
  if (!companyMemo || !companyMemo.content) return []
  const urlRegex = /(https?:\/\/[\w/:%#\$&\?\(\)~\.=\+\-]+)/g
  const nonUrlRegex = /[^:\w!?/+\-_~;.,*&@#$%()'[\]]+/g

  if (!urlRegex.test(companyMemo.content)) return [companyMemo.content]

  const putContentPart = (str: string, contentParts: string[]): string[] => {
    const idx = str.search(isHyperLink(str) ? nonUrlRegex : urlRegex)
    if (idx === -1) return contentParts.concat(str)
    const firstStr = str.slice(0, idx)
    const secondStr = str.slice(idx)
    return putContentPart(secondStr, contentParts.concat(firstStr))
  }

  return putContentPart(companyMemo.content, [])
}

export const isHyperLink = (str: string) => str.startsWith('http')
